import React from 'react';
import Box from '../../../components/Common/Box';

interface PortfolioMetrics {
  portfolioValue: number;
  totalValue: number;
  totalPL: number;
  totalPLPct: number;
  dayPL: number;
  dayPLPct: number;
  cashBalance: number;
  investedAmount: number;
}

interface PortfolioSummaryProps {
  metrics: PortfolioMetrics;
}

const iconBadgeStyle = (color: string): React.CSSProperties => ({
  width: '36px',
  height: '36px',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `${color}1F`,
  color,
  flexShrink: 0,
});

const labelStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
};

interface KpiCardProps {
  icon: string;
  iconColor: string;
  label: string;
  value: React.ReactNode;
  subtitle?: React.ReactNode;
}

const KpiCard: React.FC<KpiCardProps> = ({ icon, iconColor, label, value, subtitle }) => (
  <Box>
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <div style={iconBadgeStyle(iconColor)}>
          <i className='material-icons' style={{ fontSize: '18px' }}>
            {icon}
          </i>
        </div>
        <div style={labelStyle}>{label}</div>
      </div>
      <div style={{ fontSize: '24px', fontWeight: 800, color: 'var(--text-heading)', lineHeight: 1.2 }}>
        {value}
      </div>
      {subtitle && <div style={{ marginTop: '6px' }}>{subtitle}</div>}
    </div>
  </Box>
);

const TrendBadge: React.FC<{ positive: boolean; children: React.ReactNode }> = ({ positive, children }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '3px',
      fontSize: '13px',
      fontWeight: 700,
      color: positive ? '#10B981' : '#F43F5E',
    }}
  >
    <i className='material-icons' style={{ fontSize: '14px' }}>
      {positive ? 'arrow_upward' : 'arrow_downward'}
    </i>
    {children}
  </span>
);

const PortfolioSummary: React.FC<PortfolioSummaryProps> = ({ metrics }) => {
  const isPositiveTotal = metrics.totalPL >= 0;
  const isPositiveDay = metrics.dayPL >= 0;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '20px',
      }}
    >
      <KpiCard
        icon='account_balance_wallet'
        iconColor='#2563EB'
        label='Valeur du Portefeuille'
        value={
          <>
            {metrics.portfolioValue.toLocaleString('fr-FR')}{' '}
            <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-muted)' }}>FCFA</span>
          </>
        }
        subtitle={
          <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            dont {metrics.investedAmount.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} FCFA investis
          </span>
        }
      />

      <KpiCard
        icon={isPositiveTotal ? 'trending_up' : 'trending_down'}
        iconColor={isPositiveTotal ? '#10B981' : '#F43F5E'}
        label='Plus-Value Latente'
        value={
          <span style={{ color: isPositiveTotal ? '#10B981' : '#F43F5E' }}>
            {isPositiveTotal ? '+' : ''}
            {metrics.totalPL.toLocaleString('fr-FR')} FCFA
          </span>
        }
        subtitle={
          <TrendBadge positive={isPositiveTotal}>
            {isPositiveTotal ? '+' : ''}
            {metrics.totalPLPct.toFixed(2)}%
          </TrendBadge>
        }
      />

      <KpiCard
        icon='today'
        iconColor={isPositiveDay ? '#10B981' : '#F43F5E'}
        label='Performance Jour'
        value={
          <span style={{ color: isPositiveDay ? '#10B981' : '#F43F5E' }}>
            {isPositiveDay ? '+' : ''}
            {metrics.dayPL.toLocaleString('fr-FR')} FCFA
          </span>
        }
        subtitle={
          <TrendBadge positive={isPositiveDay}>
            {isPositiveDay ? '+' : ''}
            {metrics.dayPLPct.toFixed(2)}%
          </TrendBadge>
        }
      />

      <KpiCard
        icon='savings'
        iconColor='#0EA5E9'
        label='Espèces Disponibles'
        value={
          <>
            {metrics.cashBalance.toLocaleString('fr-FR')}{' '}
            <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-muted)' }}>FCFA</span>
          </>
        }
        subtitle={
          <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            Total avec portefeuille : {metrics.totalValue.toLocaleString('fr-FR')} FCFA
          </span>
        }
      />
    </div>
  );
};

export default PortfolioSummary;
